import React from 'react';
import { Container, Card, Row, Col, Table, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLink } from 'react-router-dom';

/**
 * Methodology Page
 *
 * Accessible to all users from the header nav.
 * Explains how the system works in business terms.
 * No code references or technical implementation details.
 */

const MethodologyPage = () => {
    return (
        <Container style={{ maxWidth: 900 }} className="py-4">
            <div className="mb-4">
                <h2>
                    <FontAwesomeIcon icon="file-alt" className="me-2 text-primary" />
                    Our Methodology
                </h2>
                <p className="text-muted">
                    How Waive generates personalised, FCA-compliant pension advice.
                </p>
            </div>

            {/* ============================================================ */}
            {/* OVERVIEW */}
            {/* ============================================================ */}
            <Card className="mb-4">
                <Card.Body>
                    <h5>
                        <FontAwesomeIcon icon="route" className="me-2 text-primary" />
                        How It Works
                    </h5>
                    <p>
                        Waive uses a structured, four-step process to generate pension advice
                        that meets FCA suitability requirements. Every recommendation is
                        deterministic (the same inputs always produce the same outputs) and
                        fully auditable.
                    </p>
                    <Row className="text-center mt-4">
                        <Col xs={6} md={3} className="mb-3">
                            <div className="p-3 bg-light rounded">
                                <FontAwesomeIcon icon="user-shield" size="2x" className="text-primary mb-2" />
                                <div className="fw-bold">1. Risk Profile</div>
                                <div className="small text-muted">Assess tolerance and capacity</div>
                            </div>
                        </Col>
                        <Col xs={6} md={3} className="mb-3">
                            <div className="p-3 bg-light rounded">
                                <FontAwesomeIcon icon="chart-pie" size="2x" className="text-primary mb-2" />
                                <div className="fw-bold">2. Fund Selection</div>
                                <div className="small text-muted">Match to suitable portfolio</div>
                            </div>
                        </Col>
                        <Col xs={6} md={3} className="mb-3">
                            <div className="p-3 bg-light rounded">
                                <FontAwesomeIcon icon="pound-sign" size="2x" className="text-primary mb-2" />
                                <div className="fw-bold">3. Contribution</div>
                                <div className="small text-muted">Optimise across wrappers</div>
                            </div>
                        </Col>
                        <Col xs={6} md={3} className="mb-3">
                            <div className="p-3 bg-light rounded">
                                <FontAwesomeIcon icon="balance-scale" size="2x" className="text-primary mb-2" />
                                <div className="fw-bold">4. Compliance</div>
                                <div className="small text-muted">Validate against FCA rules</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* ============================================================ */}
            {/* STEP 1: RISK ASSESSMENT */}
            {/* ============================================================ */}
            <Card className="mb-4">
                <Card.Body>
                    <h5>
                        <FontAwesomeIcon icon="user-shield" className="me-2 text-primary" />
                        Step 1: Risk Assessment
                    </h5>
                    <p>
                        The risk assessment combines two factors to determine a client's
                        maximum suitable investment risk level:
                    </p>

                    <Row className="mb-3">
                        <Col md={6}>
                            <div className="p-3 bg-light rounded h-100">
                                <div className="fw-bold mb-2">Risk Tolerance (40%)</div>
                                <p className="small mb-0">
                                    How comfortable is the client with investment volatility?
                                    Measured through 5 questions covering investment time horizon,
                                    emergency fund status, investment objectives, reaction to losses,
                                    and acceptable loss threshold.
                                </p>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="p-3 bg-light rounded h-100">
                                <div className="fw-bold mb-2">Capacity for Loss (60%)</div>
                                <p className="small mb-0">
                                    Can the client financially absorb investment losses?
                                    Measured through 4 questions covering lifestyle impact,
                                    near-term income needs, income security, and retirement
                                    goal flexibility.
                                </p>
                            </div>
                        </Col>
                    </Row>

                    <p>
                        Capacity for loss carries more weight (60%) than tolerance (40%) because
                        a client may be psychologically comfortable with risk but not financially
                        able to absorb losses. The combined score maps to a risk category:
                    </p>

                    <Table size="sm" bordered className="mb-3">
                        <thead className="table-light">
                        <tr>
                            <th>Risk Category</th>
                            <th>SRRI</th>
                            <th>Portfolio Level</th>
                            <th>Description</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr><td>Very Low</td><td>1</td><td>Defensive</td><td>Capital preservation focus</td></tr>
                        <tr><td>Low</td><td>2</td><td>Defensive</td><td>Minimal volatility</td></tr>
                        <tr><td>Low-Medium</td><td>3</td><td>Cautious</td><td>Conservative growth</td></tr>
                        <tr><td>Medium</td><td>4</td><td>Cautious-Balanced</td><td>Moderate risk with inflation protection</td></tr>
                        <tr><td>Medium-High</td><td>5-6</td><td>Balanced to Growth</td><td>Growth-oriented allocation</td></tr>
                        <tr><td>High</td><td>7</td><td>Growth</td><td>Accepts higher volatility for returns</td></tr>
                        </tbody>
                    </Table>

                    <Alert variant="info" className="mb-0">
                        <FontAwesomeIcon icon="info-circle" className="me-2" />
                        <strong>Financial adjustments:</strong> The capacity score can be further reduced
                        (never increased) based on circumstances such as proximity to retirement,
                        number of dependants, and financial flexibility.
                    </Alert>
                </Card.Body>
            </Card>

            {/* ============================================================ */}
            {/* STEP 2: FUND SELECTION */}
            {/* ============================================================ */}
            <Card className="mb-4">
                <Card.Body>
                    <h5>
                        <FontAwesomeIcon icon="chart-pie" className="me-2 text-primary" />
                        Step 2: Fund Selection
                    </h5>
                    <p>
                        The client's risk category determines their portfolio allocation using
                        the Managed Passive Portfolio matrix. All five L&G index funds are
                        included in every portfolio. The allocation weighting controls the
                        overall portfolio risk, not the exclusion of individual funds.
                    </p>

                    <Table size="sm" bordered responsive className="mb-3">
                        <thead className="table-light">
                        <tr>
                            <th>Level</th>
                            <th>UK Equity</th>
                            <th>World ex-UK</th>
                            <th>EM Equity</th>
                            <th>Global Bonds</th>
                            <th>Cash</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr><td>3 (Defensive)</td><td>5.7%</td><td>14.5%</td><td>2.8%</td><td>59.7%</td><td>17.3%</td></tr>
                        <tr><td>4 (Cautious)</td><td>9.1%</td><td>22.9%</td><td>4.4%</td><td>49.3%</td><td>14.3%</td></tr>
                        <tr><td>5 (Cautious-Bal)</td><td>12.2%</td><td>30.8%</td><td>5.9%</td><td>39.6%</td><td>11.5%</td></tr>
                        <tr><td>6 (Balanced)</td><td>15.3%</td><td>38.4%</td><td>7.4%</td><td>30.2%</td><td>8.8%</td></tr>
                        <tr><td>7 (Bal-Growth)</td><td>18.2%</td><td>45.9%</td><td>8.8%</td><td>21.0%</td><td>6.1%</td></tr>
                        <tr><td>8 (Growth)</td><td>21.2%</td><td>53.2%</td><td>10.3%</td><td>11.9%</td><td>3.5%</td></tr>
                        </tbody>
                    </Table>

                    <p className="small text-muted mb-0">
                        A cautious client may still hold a small percentage of equity funds because
                        the low weighting makes the overall portfolio appropriate for their risk level.
                        The allocation percentages are configurable per provider through the admin panel.
                    </p>
                </Card.Body>
            </Card>

            {/* ============================================================ */}
            {/* STEP 3: CONTRIBUTION */}
            {/* ============================================================ */}
            <Card className="mb-4">
                <Card.Body>
                    <h5>
                        <FontAwesomeIcon icon="pound-sign" className="me-2 text-primary" />
                        Step 3: Contribution Recommendation
                    </h5>

                    <p className="fw-bold">Wrapper Priority Order</p>
                    <p>
                        Contributions are allocated across tax wrappers in this order:
                    </p>
                    <ol>
                        <li className="mb-2"><strong>Pension (employer match first)</strong> — capture any free employer contributions, plus benefit from tax relief</li>
                        <li className="mb-2"><strong>ISA</strong> — tax-free growth and flexible access, up to £20,000 per year</li>
                        <li className="mb-2"><strong>Additional pension</strong> — if ISA allowance is used, further pension within annual allowance</li>
                        <li className="mb-2"><strong>GIA</strong> — overflow when tax-advantaged wrappers are full</li>
                    </ol>

                    <p className="fw-bold mt-4">Affordability Limits</p>
                    <p>
                        The system applies Consumer Duty affordability limits to ensure
                        recommendations don't cause financial hardship:
                    </p>
                    <Table size="sm" bordered className="mb-3">
                        <thead className="table-light">
                        <tr>
                            <th>Limit</th>
                            <th>Default</th>
                            <th>Purpose</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr><td>Max % of net income</td><td>15%</td><td>Ceiling on take-home pay</td></tr>
                        <tr><td>Max % of disposable</td><td>50%</td><td>Ceiling per wrapper</td></tr>
                        <tr><td>Max total % of disposable</td><td>80%</td><td>Combined ceiling across all wrappers</td></tr>
                        <tr><td>Minimum contribution</td><td>£50/month</td><td>Floor for cost-effectiveness</td></tr>
                        </tbody>
                    </Table>

                    <Alert variant="success" className="mb-0">
                        <FontAwesomeIcon icon="shield-alt" className="me-2" />
                        <strong>Never-reduce rule:</strong> The system will never recommend a lower
                        contribution than the client is currently making. If the algorithm suggests
                        less, the current level is confirmed as suitable and any surplus capacity
                        is directed to ISA.
                    </Alert>
                </Card.Body>
            </Card>

            {/* ============================================================ */}
            {/* PENSION PROJECTION */}
            {/* ============================================================ */}
            <Card className="mb-4">
                <Card.Body>
                    <h5>
                        <FontAwesomeIcon icon="calculator" className="me-2 text-primary" />
                        Pension Projection
                    </h5>
                    <p>
                        The projection shows three growth scenarios to illustrate the range
                        of possible outcomes:
                    </p>

                    <Table size="sm" bordered className="mb-3">
                        <thead className="table-light">
                        <tr>
                            <th>Scenario</th>
                            <th>Growth Rate</th>
                            <th>Drawdown Growth</th>
                            <th>Purpose</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr><td>Cautious</td><td>2% per year</td><td>1%</td><td>Conservative, near-inflation returns</td></tr>
                        <tr><td>Moderate</td><td>5% per year</td><td>3%</td><td>Typical blended portfolio</td></tr>
                        <tr><td>Adventurous</td><td>8% per year</td><td>4%</td><td>Equity-heavy portfolio</td></tr>
                        </tbody>
                    </Table>

                    <p className="fw-bold">How the numbers are calculated:</p>

                    <div className="p-3 bg-light rounded mb-3">
                        <p className="mb-2"><strong>Pot at retirement:</strong> Each year, the current pot plus annual
                            contributions grows by the scenario rate. This compounds over the full
                            investment period.</p>

                        <p className="mb-2"><strong>Annuity income:</strong> 6% of the pot at retirement. This is a
                            simplified rate for illustration; actual annuity rates depend on age,
                            health, and market conditions.</p>

                        <p className="mb-0"><strong>Drawdown duration:</strong> Assumes withdrawing 50% of current salary
                            each year in retirement, with the remaining pot growing at a lower rate.
                            Shows the age at which the pot would be exhausted. A warning appears if
                            this is before age 85.</p>
                    </div>

                    <Alert variant="warning" className="mb-0">
                        <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                        <strong>Important:</strong> These projections are for illustration only. They do not
                        account for inflation, tax-free lump sum, state pension, contribution
                        escalation, or fund charges. Figures are shown in future money terms.
                    </Alert>
                </Card.Body>
            </Card>

            {/* ============================================================ */}
            {/* STEP 4: COMPLIANCE */}
            {/* ============================================================ */}
            <Card className="mb-4">
                <Card.Body>
                    <h5>
                        <FontAwesomeIcon icon="balance-scale" className="me-2 text-primary" />
                        Step 4: Regulatory Compliance
                    </h5>
                    <p>
                        Every recommendation is validated against FCA regulatory requirements
                        before being presented to the client:
                    </p>

                    <Table size="sm" bordered className="mb-0">
                        <thead className="table-light">
                        <tr>
                            <th>Regulation</th>
                            <th>Requirement</th>
                            <th>How We Meet It</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>COBS 9.2.1R</td>
                            <td>Suitability</td>
                            <td>Portfolio allocation matched to client risk profile</td>
                        </tr>
                        <tr>
                            <td>COBS 9.2.2R</td>
                            <td>Assessment</td>
                            <td>Tolerance and capacity evaluation with weighted scoring</td>
                        </tr>
                        <tr>
                            <td>COBS 9.4</td>
                            <td>Suitability reports</td>
                            <td>Full provenance trail and printable suitability report</td>
                        </tr>
                        <tr>
                            <td>PRIN 2A</td>
                            <td>Consumer Duty</td>
                            <td>Affordability limits and wrapper optimisation</td>
                        </tr>
                        <tr>
                            <td>COBS 4.2</td>
                            <td>Clear communication</td>
                            <td>Plain language explanations with AI-generated summaries</td>
                        </tr>
                        <tr>
                            <td>COBS 6.1</td>
                            <td>Cost disclosure</td>
                            <td>TER charges and tax relief displayed for every fund</td>
                        </tr>
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* ============================================================ */}
            {/* FOOTER */}
            {/* ============================================================ */}
            <div className="text-center text-muted small py-3">
                <p className="mb-1">
                    All scoring weights, risk bands, allocation percentages, and contribution
                    limits are configurable per provider through the admin panel.
                </p>
                <p className="mb-0">
                    This is a POC demonstration and does not constitute financial advice.
                </p>
            </div>
        </Container>
    );
};

export default MethodologyPage;